import { stringify } from 'querystring';
import { useRouter } from 'next/navigation';
import type { MapRef } from 'react-map-gl/mapbox';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import {
  updateAdminFilters,
  updateBoundingBox,
  updateDistanceAway,
  updateExploreActionType,
  updateFilters,
  updateLocationResult
} from '@alltrails/explore-service/redux/exploreServiceSlice';
import type ExploreEventData from '@alltrails/explore-service/types/ExploreEventData';
import type ExploreServiceFilters from '@alltrails/explore-service/types/ExploreServiceFilters';
import { bottomRightLatParamKey, bottomRightLngParamKey, topLeftLatParamKey, topLeftLngParamKey } from '@alltrails/filters/utils/exploreSearchParams';
import { defaultPOIZoom } from '@alltrails/maps/utils/constants';
import { getUrl } from '@alltrails/search/utils/algoliaResultUtils';
import type { AlgoliaSearchResult } from '@alltrails/types/AlgoliaResults';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import { explorePath } from '@/utils/constants';
import getMapBoundsFromSlug from '@/utils/getMapBoundsFromSlug';
import { AppDispatch } from '@/utils/redux';
import { setStreetAddressLngLat } from '@/utils/slices/exploreSlice';

export function handleAlgoliaResultSelect(
  result: AlgoliaSearchResult,
  languageRegionCode: LanguageRegionCode,
  dispatch: AppDispatch,
  router: ReturnType<typeof useRouter>,
  searchParams: URLSearchParams,
  map?: MapRef,
  onResultSelect?: (result: AlgoliaSearchResult) => void,
  navigateToPOI: boolean = false,
  navigateToPlace: boolean = false,
  navigateToTrail: boolean = false
) {
  dispatch(updateDistanceAway(undefined));
  dispatch(setStreetAddressLngLat(undefined));
  dispatch(updateAdminFilters({}));

  switch (result.type) {
    case 'area':
    case 'place': {
      dispatch(updateExploreActionType('search result clicked'));
      dispatch(updateBoundingBox(undefined)); // Clear the bounding box so that results can be fetched solely based on the location result
      dispatch(updateLocationResult(result));
      onResultSelect?.(result);

      getMapBoundsFromSlug(result.slug)
        .then(({ boundingBox }) => {
          if (navigateToPlace) {
            const params = {
              [bottomRightLatParamKey]: boundingBox.bottomRight.lat.toString(),
              [bottomRightLngParamKey]: boundingBox.bottomRight.lng.toString(),
              [topLeftLatParamKey]: boundingBox.topLeft.lat.toString(),
              [topLeftLngParamKey]: boundingBox.topLeft.lng.toString()
            };
            const stringifiedParams = stringify(params);
            const searchParams = new URLSearchParams(stringifiedParams);
            router.push(`${wrapUrlSafe(`${explorePath}/${result.slug}`, languageRegionCode)}?${decodeURIComponent(searchParams.toString())}`);
          } else {
            const eventData: ExploreEventData = { boundingBox, mapRotation: 0, preserveLocationResult: true };
            map?.fitBounds(
              [
                [boundingBox.bottomLeft.lng, boundingBox.bottomLeft.lat],
                [boundingBox.topRight.lng, boundingBox.topRight.lat]
              ],
              {},
              eventData
            );
          }
        })
        .catch(() => {
          // Handle any errors we may get when trying to fetch map bounds.
          // This should never really happen, but want to have some graceful handling just in case.
          dispatch(updateLocationResult(undefined));
          if (result._geoloc) {
            map?.flyTo({ center: result._geoloc });
          }
        });
      break;
    }
    case 'filter': {
      dispatch(updateExploreActionType('filter applied'));
      dispatch(updateFilters(result.filters as ExploreServiceFilters));
      break;
    }
    case 'poi': {
      dispatch(updateExploreActionType('search result clicked'));

      if (navigateToPOI) {
        router.push(wrapUrlSafe(`${explorePath}/${result.slug}?${searchParams}`, languageRegionCode));
      } else {
        dispatch(updateBoundingBox(undefined)); // Clear the bounding box so that results can be fetched solely based on the location result
        dispatch(updateLocationResult(result));

        const eventData: ExploreEventData = { preserveLocationResult: true };
        map?.flyTo({ center: result._geoloc, zoom: defaultPOIZoom }, eventData);
      }
      onResultSelect?.(result);
      break;
    }
    case 'trail': {
      dispatch(updateExploreActionType('search result clicked'));
      onResultSelect?.(result);
      if (navigateToTrail) {
        router.push(wrapUrlSafe(`${explorePath}/${result.slug}`, languageRegionCode));
      } else {
        window.location.assign(getUrl(result, languageRegionCode, true));
      }
      break;
    }
    default: {
      window.location.assign(getUrl(result, languageRegionCode, true));
      break;
    }
  }
}
